/**
 * Панель отладки для сообщений AI
 * Показывает детали: модель, токены, время, вызовы инструментов
 */

import { useState } from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import {
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MessageMetadata, ToolCallWithResult } from './types';

interface DebugPanelProps {
  metadata?: MessageMetadata;
  className?: string;
  /** Слот для дополнительных действий (например, кнопка перегенерации) */
  actionSlot?: React.ReactNode;
}

/**
 * Форматирование чисел
 */
function formatNumber(value: number | undefined): string {
  if (value === undefined) {return '—';}
  return value.toLocaleString('ru-RU');
}

/**
 * Форматирование времени в мс
 */
function formatDuration(ms: number | undefined): string {
  if (ms === undefined) {return '—';}
  if (ms < 1000) {return `${ms}мс`;}
  return `${(ms / 1000).toFixed(1)}с`;
}

/**
 * Компонент отображения одного вызова инструмента
 */
function ToolCallItem({ toolCall }: { toolCall: ToolCallWithResult }) {
  const [isOpen, setIsOpen] = useState(false);
  const hasError = !!toolCall.error;

  // Парсим аргументы для отображения
  let parsedArgs: Record<string, unknown> = {};
  try {
    parsedArgs = JSON.parse(toolCall.arguments);
  } catch {
    parsedArgs = { raw: toolCall.arguments };
  }

  // Парсим результат для отображения
  let parsedResult: unknown = toolCall.result;
  if (toolCall.result) {
    try {
      parsedResult = JSON.parse(toolCall.result);
    } catch {
      // Оставляем как строку
    }
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 w-full text-left py-1 hover:bg-muted/50 rounded px-1 -mx-1">
        <ChevronRight
          className={cn(
            'h-3 w-3 text-muted-foreground transition-transform',
            isOpen && 'rotate-90'
          )}
        />
        {/* Статус: точка вместо иконки */}
        <span
          className={cn(
            'w-1.5 h-1.5 rounded-full flex-shrink-0',
            hasError ? 'bg-destructive' : 'bg-green-500'
          )}
        />
        <span className="text-xs font-mono truncate flex-1">{toolCall.name}</span>
        {toolCall.duration !== undefined && (
          <span className="text-[10px] text-muted-foreground">
            {formatDuration(toolCall.duration)}
          </span>
        )}
      </CollapsibleTrigger>

      <CollapsibleContent className="pt-1">
        <div className="max-h-64 overflow-y-auto pl-5 pr-1">
          <div className="space-y-2">
            {/* Аргументы */}
            <div className="overflow-hidden">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                Аргументы:
              </span>
              <pre className="text-[10px] bg-muted/50 rounded p-1.5 mt-0.5 whitespace-pre-wrap break-all">
                {JSON.stringify(parsedArgs, null, 2)}
              </pre>
            </div>

            {/* Результат или ошибка */}
            {hasError ? (
              <div className="overflow-hidden">
                <span className="text-[10px] text-destructive uppercase tracking-wide">
                  Ошибка:
                </span>
                <pre className="text-[10px] bg-destructive/10 text-destructive rounded p-1.5 mt-0.5 whitespace-pre-wrap break-all">
                  {toolCall.error}
                </pre>
              </div>
            ) : (toolCall.result ? (
              <div className="overflow-hidden">
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide">
                  Результат:
                </span>
                <pre className="text-[10px] bg-muted/50 rounded p-1.5 mt-0.5 whitespace-pre-wrap break-all">
                  {typeof parsedResult === 'object'
                    ? JSON.stringify(parsedResult, null, 2)
                    : String(parsedResult)}
                </pre>
              </div>
            ) : null)}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * Главный компонент панели отладки
 */
export function DebugPanel({ metadata, className, actionSlot }: DebugPanelProps) {
  const [isOpen, setIsOpen] = useState(false);

  if (!metadata) {return null;}

  const hasToolCalls = metadata.toolCalls && metadata.toolCalls.length > 0;
  const hasTokens = metadata.tokens && (metadata.tokens.total || metadata.tokens.prompt);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={className}>
      <div className="flex items-center justify-between gap-2 mt-1">
        <CollapsibleTrigger className="flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors">
          <ChevronDown
            className={cn(
              'h-3 w-3 transition-transform',
              isOpen && 'rotate-180'
            )}
          />
          <span>Детали</span>
          {metadata.routerUsed && (
            <Badge variant="outline" className="h-4 px-1 text-[9px]">
              роутер
            </Badge>
          )}
          {metadata.webSearchUsed && (
            <Badge variant="outline" className="h-4 px-1 text-[9px]">
              веб
            </Badge>
          )}
          {hasToolCalls && (
            <Badge variant="outline" className="h-4 px-1 text-[9px]">
              {metadata.toolCalls!.length} инстр.
            </Badge>
          )}
        </CollapsibleTrigger>

        {/* Слот для действий (кнопка перегенерации) */}
        {actionSlot}
      </div>

      <CollapsibleContent className="mt-2 space-y-3 text-xs">
        {/* Основная информация */}
        <div className="space-y-1 text-[11px]">
          {/* Модель */}
          {metadata.model && (
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground">Модель:</span>
              <span className="font-mono truncate">{metadata.model.split('/').pop()}</span>
            </div>
          )}

          {/* Время */}
          {metadata.duration !== undefined && (
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground">Время:</span>
              <span>{formatDuration(metadata.duration)}</span>
            </div>
          )}

          {/* Токены */}
          {hasTokens && (
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground">Токены:</span>
              <span>
                {formatNumber(metadata.tokens?.prompt)} вход → {formatNumber(metadata.tokens?.completion)} выход
                {metadata.tokens?.total && (
                  <span className="text-muted-foreground">
                    {' '}(всего: {formatNumber(metadata.tokens.total)})
                  </span>
                )}
              </span>
            </div>
          )}
        </div>

        {/* Решение роутера */}
        {metadata.routerUsed && metadata.routerDecision && (
          <div className="bg-muted/50 rounded-md p-2 space-y-1">
            <div className="text-[11px] font-medium">
              Роутер
            </div>
            {metadata.routerDecision.category && (
              <div className="text-[10px] text-muted-foreground">
                Категория: <span className="font-medium">{metadata.routerDecision.category}</span>
              </div>
            )}
            {metadata.routerDecision.reasoning && (
              <div className="text-[10px] text-muted-foreground">
                {metadata.routerDecision.reasoning}
              </div>
            )}
          </div>
        )}

        {/* Источники веб-поиска */}
        {metadata.webSearchUsed && metadata.webSearchSources && metadata.webSearchSources.length > 0 && (
          <div className="bg-muted/50 rounded-md p-2 space-y-1">
            <div className="text-[11px] font-medium">
              Источники веб-поиска
            </div>
            <ul className="space-y-0.5">
              {metadata.webSearchSources.map((source, i) => (
                <li key={i} className="text-[10px]">
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground hover:underline truncate block"
                  >
                    {source.title || source.url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Вызовы инструментов */}
        {hasToolCalls && (
          <div className="space-y-1">
            <div className="text-[11px] font-medium">
              Вызовы инструментов ({metadata.toolCalls!.length})
            </div>
            <div className="space-y-0.5 pl-1">
              {metadata.toolCalls!.map((tc, i) => (
                <ToolCallItem key={tc.id || i} toolCall={tc} />
              ))}
            </div>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
